import { Box, Group, Menu, Text, UnstyledButton } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { tokens as T } from '../theme/tokens';
import { useUIStore } from '../stores/uiStore';
import { useTenantsStore } from '../stores/dataStores';
import { rowsOf } from '../stores/collection';
import { useAuthStore } from '../auth/authStore';
import { signOut } from '../auth/AuthProvider';
import { EstatePill } from '../ui';

const ENV_LABEL = 'AGENTIC-ROUTER-PROD · US-CENTRAL1';

/** The tenant scope picker.
 *
 * A Menu, not a Popover: Menu.Label, Menu.Item, outside-click, Escape and
 * roving focus all come with it, and every one of those is something the
 * design's hand-rolled dropdown would make us write. */
function ScopePicker() {
  const scope = useUIStore((s) => s.scope);
  const setScope = useUIStore((s) => s.setScope);
  const tenants = rowsOf(useTenantsStore((s) => s.data));
  const current = tenants.find((t) => t.key === scope);
  const scoped = Boolean(scope);

  return (
    <Menu
      position="bottom-end"
      width={300}
      radius="lg"
      offset={8}
      shadow="0 18px 40px rgba(46,42,38,0.18)"
      styles={{
        dropdown: { background: T.card, border: `1px solid ${T.border}`, padding: 8 },
        label: {
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.09em',
          color: T.ink5,
          padding: '8px 10px 6px',
        },
        item: { borderRadius: 9, padding: '9px 10px' },
      }}
    >
      <Menu.Target>
        <UnstyledButton
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            background: scoped ? T.ink : T.card,
            border: `1px solid ${scoped ? T.ink : T.border}`,
            color: scoped ? T.card : T.ink3,
            fontSize: 12,
            fontWeight: 700,
            padding: '7px 13px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
          }}
        >
          <Box
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: scoped ? T.coral : T.teal,
            }}
          />
          {current ? current.displayName : 'All tenants'}
          <Box component="span" style={{ opacity: 0.6 }}>
            ▾
          </Box>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>SWITCH INTO</Menu.Label>
        {tenants.map((t) => (
          <Menu.Item
            key={t.key}
            bg={scope === t.key ? T.navActive : undefined}
            onClick={() => setScope(t.key)}
            rightSection={<EstatePill estate={t.estateId} />}
          >
            <Text fz={13} fw={700} c={T.ink}>
              {t.displayName}
            </Text>
            <Text ff="monospace" fz={11} c={T.ink5} mt={1}>
              {t.key}
            </Text>
          </Menu.Item>
        ))}
        <Menu.Item
          bg={scope === null ? T.navActive : undefined}
          onClick={() => setScope(null)}
          rightSection={
            <Text fz={10} fw={800} c={T.neutralFg} style={{ letterSpacing: '0.06em' }}>
              ALL
            </Text>
          }
        >
          <Text fz={13} fw={700} c={T.ink}>
            All tenants
          </Text>
          <Text ff="monospace" fz={11} c={T.ink5} mt={1}>
            operator view — every estate
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export function Header({ preview }: { preview?: boolean }) {
  const navigate = useNavigate();
  const email = useAuthStore((s) => s.email);
  const displayName = useAuthStore((s) => s.displayName);

  return (
    <Group
      justify="space-between"
      align="center"
      gap={16}
      wrap="nowrap"
      px={18}
      py={10}
      style={{ background: T.page }}
    >
      <Group
        gap={10}
        wrap="nowrap"
        style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          padding: '9px 14px',
          width: 'min(420px, 40%)',
        }}
      >
        <Text fz={11} fw={600} c={T.muted} style={{ letterSpacing: '0.04em' }}>
          ⌘K
        </Text>
        <Text fz={14} c={T.muted} truncate>
          Search estates, tenants, pool keys, tokens, event ids…
        </Text>
      </Group>

      <Group gap={14} wrap="nowrap">
        <ScopePicker />
        {preview ? (
          <Box
            style={{
              background: T.coralSoft,
              color: T.coralDeep,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              padding: '6px 12px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            PREVIEW · SAMPLE DATA
          </Box>
        ) : null}
        <Box
          style={{
            background: T.marigoldSoft,
            color: T.marigoldFg,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            padding: '6px 12px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
          }}
        >
          {ENV_LABEL}
        </Box>
        <Menu position="bottom-end" width={200} radius="lg" offset={8}>
          <Menu.Target>
            <UnstyledButton>
              <Group gap={10} wrap="nowrap">
                <Box
                  style={{ width: 34, height: 34, borderRadius: 999, background: T.faint }}
                />
                <Box style={{ lineHeight: 1.15 }}>
                  <Text fz={14} fw={700} style={{ whiteSpace: 'nowrap' }}>
                    {displayName ?? email ?? 'Preview operator'}
                  </Text>
                  <Text fz={12} c={T.ink4} style={{ whiteSpace: 'nowrap' }}>
                    {email ? 'Super Admin' : 'not signed in'}
                  </Text>
                </Box>
                <Text fz={12} c={T.ink4}>
                  ▾
                </Text>
              </Group>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {email ? (
              <Menu.Item onClick={() => void signOut()}>Sign out</Menu.Item>
            ) : (
              // /login is outside both sections on purpose — it belongs to neither.
              <Menu.Item onClick={() => navigate('/login')}>Sign in</Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
